import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { upsertJsonLd, removeJsonLd } from '../../seo/useSeo'
import { track } from '../../analytics/track'
import {
  BoltIcon,
  CheckCircleIcon,
  EuroIcon,
  HandshakeIcon,
  HouseSolarIcon,
  PhoneOffIcon,
  ScaleIcon,
  SearchIcon,
  ShieldCheckIcon,
  SunIcon,
  TargetIcon,
} from './icons'
import { MarketingHeader } from './MarketingHeader'
import { MarketingFooter } from './MarketingFooter'
import { PvKostenRechnerWidget } from './PvKostenRechnerWidget'
import { usePriceCalculatorCta } from './usePriceCalculatorCta'
import { getLandingPagesByTopic } from '../../content/landingPages'
import { blogListPath } from '../../content/blog'
import { resolveSupportedLang } from '../../i18n'

interface LandingPageProps {
  onSelectCustomer: () => void
  onSelectCraftsman: () => void
  onLoginCustomer: () => void
  onLoginCraftsman: () => void
}

interface Step {
  title: string
  text: string
}

interface FaqItem {
  q: string
  a: string
}

const FAQ_JSON_LD_ID = 'landing-faq-jsonld'

export function LandingPage({ onSelectCustomer, onSelectCraftsman, onLoginCustomer, onLoginCraftsman }: LandingPageProps) {
  const { t, i18n } = useTranslation()
  const trustItems = t('landing.hero.trust', { returnObjects: true }) as string[]
  const customerSteps = t('landing.steps.items', { returnObjects: true }) as Step[]
  const comparisonBad = t('landing.comparison.bad.items', { returnObjects: true }) as string[]
  const comparisonGood = t('landing.comparison.good.items', { returnObjects: true }) as string[]
  const faqItems = t('landing.faq.items', { returnObjects: true }) as FaqItem[]
  // Separate section from faqItems: the general FAQ above is customer-phrased throughout ("Sie" as
  // the customer) and doesn't answer typical craftsman questions like "how many leads/month" or
  // the fee model from the craftsman's own perspective (docs/tasks/active/ux/roofora-task-022-suggestion-
  // 02-ux-handwerker.md, point 5). Kept as a distinct i18n key rather than appended to landing.faq
  // so the two audiences stay visually and semantically separate on the shared homepage.
  const craftsmanFaqItems = t('landing.craftsmanFaq.items', { returnObjects: true }) as FaqItem[]
  const lang = resolveSupportedLang(i18n.resolvedLanguage)

  // Homepage FAQ (rendered below in the "faq"/"craftsman-faq" sections) had a visible <details>
  // FAQ but no matching FAQPage rich-result markup - unlike the SEO landing pages/pv-kostenrechner,
  // which already emit one via useLandingPageSeo/usePvKostenRechnerSeo (see docs/tasks/active/seo/
  // seo-nach-recherche.md #7). Keyed off i18n.resolvedLanguage rather than the faqItems/
  // craftsmanFaqItems arrays themselves, since t(..., { returnObjects: true }) returns a fresh
  // array reference on every render - that would otherwise re-run this effect on every re-render.
  useEffect(() => {
    const items = [
      ...(t('landing.craftsmanFaq.items', { returnObjects: true }) as FaqItem[]),
      ...(t('landing.faq.items', { returnObjects: true }) as FaqItem[]),
    ]
    upsertJsonLd(FAQ_JSON_LD_ID, {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: items.map((item) => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: { '@type': 'Answer', text: item.a },
      })),
    })
    return () => removeJsonLd(FAQ_JSON_LD_ID)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.resolvedLanguage])
  // The planning-guide hub page exists in all 3 languages (see content/landingPages) - link to
  // the one matching the current UI language rather than hardcoding the German URL.
  const guideTeaserHref = `/${getLandingPagesByTopic('photovoltaik-planen').find((page) => page.lang === lang)?.slug}`
  // GitLab-Issue #143: same lookup pattern as guideTeaserHref above - the Kunden-How-to-Guide
  // exists in all 3 languages (see content/landingPages/so-funktioniert-es-fuer-kunden.ts).
  const howToCustomerHref = `/${getLandingPagesByTopic('so-funktioniert-es-fuer-kunden').find((page) => page.lang === lang)?.slug}`
  // GitLab-Issue #144: Handwerker-Pendant zu howToCustomerHref oben.
  const howToCraftsmanHref = `/${getLandingPagesByTopic('so-funktioniert-es-fuer-handwerker').find((page) => page.lang === lang)?.slug}`
  const blogTeaserHref = blogListPath(lang)
  const handleCalculatorRequestOffer = usePriceCalculatorCta(onSelectCustomer)

  // Same "cta_clicked" event as the nav CTA in MarketingHeader.tsx, tagged with a distinct
  // `location` per placement so the funnel-position breakdown is visible in Umami.
  function trackedSelectCustomer(location: string) {
    return () => {
      track('cta_clicked', { role: 'customer', location })
      onSelectCustomer()
    }
  }
  function trackedSelectCraftsman(location: string) {
    return () => {
      track('cta_clicked', { role: 'craftsman', location })
      onSelectCraftsman()
    }
  }
  function trackedCalculatorRequestOffer(values: Parameters<typeof handleCalculatorRequestOffer>[0]) {
    track('cta_clicked', { role: 'customer', location: 'price_calculator_result' })
    handleCalculatorRequestOffer(values)
  }
  // GitLab-Issue #143: separate "how_to_clicked" event (not "cta_clicked") - this is a navigation
  // link to a content page, not a signup/registration action, so it needs its own funnel metric
  // rather than diluting the existing customer/craftsman CTA counts.
  function trackHowToCustomer() {
    track('how_to_clicked', { role: 'customer', location: 'hero' })
  }
  // GitLab-Issue #144: Handwerker-Pendant zu trackHowToCustomer oben.
  function trackHowToCraftsman() {
    track('how_to_clicked', { role: 'craftsman', location: 'hero' })
  }

  return (
    <div className="landing">
      <MarketingHeader
        logoHref="#top"
        onSelectCustomer={onSelectCustomer}
        onLoginCustomer={onLoginCustomer}
        onLoginCraftsman={onLoginCraftsman}
      />

      <main>
        <section className="hero" id="top">
          <div className="hero-content">
            <p className="eyebrow">{t('landing.hero.eyebrow')}</p>
            <h1>{t('landing.hero.title')}</h1>
            <p className="hero-sub">
              {t('landing.hero.subtitlePrefix')} <strong>{t('landing.hero.subtitleYou')}</strong>{' '}
              {t('landing.hero.subtitleSuffix')}
            </p>
            {/* Placed before the account-creation CTAs on purpose: visitors who aren't ready to
             *  create an account yet get a zero-commitment first step - free, no email/login
             *  required for the calculation itself (see usePvRechnerSeo.ts/PvRechnerPage.tsx). */}
            <Link to="/pv-rechner" className="hero-pv-banner">
              <span className="hero-pv-banner-icon">
                <TargetIcon />
              </span>
              <span className="hero-pv-banner-text">
                <span className="hero-pv-banner-badge">{t('landing.pvToolTeaser.badge')}</span>
                <strong>{t('landing.hero.pvToolBannerTitle')}</strong>
              </span>
              <span className="hero-pv-banner-arrow" aria-hidden="true">
                →
              </span>
            </Link>
            <div className="hero-actions">
              <button className="btn-primary" onClick={trackedSelectCustomer('hero')}>
                {t('landing.hero.ctaCustomer')}
              </button>
              <button className="btn-secondary" onClick={trackedSelectCraftsman('hero')}>
                {t('landing.hero.ctaCraftsman')}
              </button>
            </div>
            {/* GitLab-Issues #143/#144 (Sub-Issues von #142): dezente How-to-Links direkt unter
                den beiden Conversion-CTAs oben, wie in #142 recherchiert/festgelegt - echte
                interne Links (kein reiner JS-Button), visuell klar von btn-primary/btn-secondary
                unterschieden. Reihenfolge (Kunde vor Handwerker) spiegelt die der CTAs darüber. */}
            <div className="hero-how-to-links">
              <Link to={howToCustomerHref} className="hero-how-to-link" onClick={trackHowToCustomer}>
                {t('landing.hero.howToCustomer')}
              </Link>
              <Link to={howToCraftsmanHref} className="hero-how-to-link" onClick={trackHowToCraftsman}>
                {t('landing.hero.howToCraftsman')}
              </Link>
            </div>
            <ul className="trust-row">
              {trustItems.map((item) => (
                <li key={item}>
                  <CheckCircleIcon /> {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="hero-visual">
            <div className="hero-roof-scene" aria-hidden="true">
              <SunIcon className="hero-roof-sun" />
              <svg className="hero-roof-outline" viewBox="0 0 200 120">
                <polyline points="8,112 100,18 192,112" />
              </svg>
              <div className="hero-roof-panels">
                <span className="hero-roof-panel hero-roof-panel--left" />
                <span className="hero-roof-panel hero-roof-panel--center" />
                <span className="hero-roof-panel hero-roof-panel--right" />
              </div>
            </div>
            <p className="hero-visual-slogan">{t('landing.slogan')}</p>
            <div className="usp-card usp-card--highlight">
              <ShieldCheckIcon className="usp-icon" />
              <h2>{t('landing.hero.highlightTitle')}</h2>
              <p>{t('landing.hero.highlightText')}</p>
              <img
                className="usp-card-illustration"
                src="/anonyme-anfrage-kontaktfreigabe.svg"
                width="360"
                height="220"
                loading="lazy"
                alt={t('landing.hero.highlightImageAlt')}
              />
            </div>
          </div>
        </section>

        {/* Erstes interaktives Element der Landingpage, noch vor der bestehenden Bedarfsanalyse
            (tasks/new-things/preis-durchschnittsrechner/03-landingpage-integration.md) - ergänzt
            die pv-tool-teaser-Sektion darunter (Preis vs. Anlagengröße), ersetzt sie nicht. */}
        <section className="price-calculator-section" id="kostenrechner">
          <div className="price-calculator-intro">
            <span className="pv-tool-teaser-badge">{t('landing.priceCalculatorSection.badge')}</span>
            <h2>{t('landing.priceCalculatorSection.title')}</h2>
            <p>{t('landing.priceCalculatorSection.subtitle')}</p>
          </div>
          <PvKostenRechnerWidget onRequestOffer={trackedCalculatorRequestOffer} />
          <p className="price-calculator-trust-explainer">{t('landing.priceCalculatorSection.trustExplainer')}</p>
          {/* Interne Verlinkung zur eigenständigen, ausführlicheren /pv-kostenrechner-Landingpage
              (eigene FAQ/Breadcrumb/WebApplication-Schema) - das eingebettete Widget oben verlinkte
              bisher nirgends dorthin (docs/tasks/active/seo/seo_v3.md #9). Deutsch-only wie /pv-rechner unten,
              da die Zielseite selbst kein i18n hat (siehe usePvKostenRechnerSeo.ts). */}
          <Link to="/pv-kostenrechner" className="price-calculator-details-link">
            {t('landing.priceCalculatorSection.detailsLinkLabel')}
          </Link>
          <p className="price-calculator-divider">{t('landing.priceCalculatorSection.divider')}</p>
        </section>

        <section className="pv-tool-teaser">
          <TargetIcon className="pv-tool-teaser-icon" />
          <div>
            <span className="pv-tool-teaser-badge">{t('landing.pvToolTeaser.badge')}</span>
            <h2>{t('landing.pvToolTeaser.title')}</h2>
            <p>{t('landing.pvToolTeaser.text')}</p>
          </div>
          <Link to="/pv-rechner" className="btn-primary">
            {t('landing.pvToolTeaser.cta')}
          </Link>
        </section>

        <section className="usp" id="vorteile">
          <div className="usp-card">
            <ScaleIcon className="usp-icon" />
            <h2>{t('landing.usp.bidding.title')}</h2>
            <p>{t('landing.usp.bidding.text')}</p>
          </div>
          <div className="usp-card">
            <PhoneOffIcon className="usp-icon" />
            <h2>{t('landing.usp.noCalls.title')}</h2>
            <p>{t('landing.usp.noCalls.text')}</p>
          </div>
          <div className="usp-card">
            <EuroIcon className="usp-icon" />
            <h2>{t('landing.usp.cheaper.title')}</h2>
            <p>{t('landing.usp.cheaper.text')}</p>
          </div>
          <div className="usp-card">
            <TargetIcon className="usp-icon" />
            <h2>{t('landing.usp.noPressure.title')}</h2>
            <p>{t('landing.usp.noPressure.text')}</p>
          </div>
        </section>

        <section className="steps" id="ablauf">
          <h2 className="section-title">{t('landing.steps.title')}</h2>
          <img
            className="steps-illustration"
            src="/vier-schritte-solaranlage.svg"
            width="800"
            height="170"
            loading="lazy"
            alt={t('landing.steps.imageAlt')}
          />
          <ol className="steps-list">
            {customerSteps.map((step, i) => (
              <li key={step.title}>
                <span className="step-number">{i + 1}</span>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="comparison">
          <h2 className="section-title">{t('landing.comparison.title')}</h2>
          <div className="comparison-grid">
            <div className="comparison-col comparison-col--bad">
              <h3>{t('landing.comparison.bad.title')}</h3>
              <ul>
                {comparisonBad.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
            <div className="comparison-col comparison-col--good">
              <h3>{t('landing.comparison.good.title')}</h3>
              <ul>
                {comparisonGood.map((item) => (
                  <li key={item}>
                    <CheckCircleIcon /> {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="craftsman-band" id="handwerker">
          <HandshakeIcon className="craftsman-band-icon" />
          <div>
            <h2>{t('landing.craftsmanBand.title')}</h2>
            <p>{t('landing.craftsmanBand.text')}</p>
          </div>
          <button className="btn-secondary" onClick={trackedSelectCraftsman('craftsman_band')}>
            {t('landing.craftsmanBand.cta')}
          </button>
        </section>

        <section className="usp">
          <div className="usp-card">
            <ShieldCheckIcon className="usp-icon" />
            <h2>{t('landing.craftsmanUsp.free.title')}</h2>
            <p>{t('landing.craftsmanUsp.free.text')}</p>
          </div>
          <div className="usp-card">
            <SearchIcon className="usp-icon" />
            <h2>{t('landing.craftsmanUsp.noBlindLeads.title')}</h2>
            <p>{t('landing.craftsmanUsp.noBlindLeads.text')}</p>
          </div>
        </section>

        <section className="faq" id="craftsman-faq">
          <h2 className="section-title">{t('landing.craftsmanFaq.title')}</h2>
          <div className="faq-list">
            {craftsmanFaqItems.map((item) => (
              <details key={item.q} className="faq-item">
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="faq" id="faq">
          <h2 className="section-title">{t('landing.faq.title')}</h2>
          <div className="faq-list">
            {faqItems.map((item) => (
              <details key={item.q} className="faq-item">
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* SEO: the blog is otherwise only reachable via the footer link (see MarketingFooter),
            which buries the site's main organic-traffic entry point - see
            tasks/suggestion/roofora-task-022-suggestion-03-marketing-legal-seo.md point 4. */}
        <section className="craftsman-band blog-teaser">
          <div>
            <h2>{t('landing.blogTeaser.title')}</h2>
            <p>{t('landing.blogTeaser.text')}</p>
          </div>
          <Link to={blogTeaserHref} className="btn-secondary">
            {t('landing.blogTeaser.cta')}
          </Link>
        </section>

        {/* SEO: same reasoning as blog-teaser above - the 5 SEO landing pages (see
            content/landingPages) were otherwise only reachable via the footer nav, not from the
            homepage body, which carries the most internal link weight on the site. Links to the
            planning guide, which itself cross-links the other 4 pages as a hub. */}
        <section className="craftsman-band guide-teaser">
          <TargetIcon className="craftsman-band-icon" />
          <div>
            <h2>{t('landing.guideTeaser.title')}</h2>
            <p>{t('landing.guideTeaser.text')}</p>
          </div>
          <Link to={guideTeaserHref} className="btn-secondary">
            {t('landing.guideTeaser.cta')}
          </Link>
        </section>

        <section className="final-cta">
          <HouseSolarIcon className="final-cta-icon" />
          <h2>{t('landing.finalCta.title')}</h2>
          <p>{t('landing.finalCta.text')}</p>
          <button className="btn-primary" onClick={trackedSelectCustomer('final_cta')}>
            <BoltIcon /> {t('landing.finalCta.cta')}
          </button>
        </section>
      </main>

      <MarketingFooter />
    </div>
  )
}
